import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Button } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioMedicoBase } from '@/features/registro/medicos/relatorios/relatorio-medico.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { CardMedicoSelecionado } from '@/features/registro/medicos/relatorios/components/card-medico-selecionado/card-medico-selecionado';
import { EtiquetaEnderecoMedicoService } from '@/core/api/sicom-api/registro/medico/relatorios/etiqueta-endereco-medico/etiqueta-endereco-medico.service';
import { ReportEtiquetaEnderecoMedicoRequest, ReportEtiquetaEnderecoMedicoResponse } from '@/core/api/sicom-api/registro/medico/relatorios/etiqueta-endereco-medico/etiqueta-endereco-medico.model';
import { EtiquetaEnderecoMedicoJobService } from '@/core/services/etiqueta-endereco-medico-job.service';
import { CanDirective } from '@/core/permissions/can.directive';

type Req = ReportEtiquetaEnderecoMedicoRequest;
type Resp = ReportEtiquetaEnderecoMedicoResponse;

@Component({
    selector: 'app-etiqueta-endereco-medico',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, TooltipModule, RelatorioContainer, Pesquisa, CardMedicoSelecionado, CanDirective],
    templateUrl: './etiqueta-endereco-medico.html',
    styleUrl: './etiqueta-endereco-medico.scss'
})
/**
 * Relatório PDF assíncrono da Etiqueta de Endereço do médico (nuCrm/sgUf).
 * Herda `RelatorioMedicoBase`: toda a orquestração de export/polling/download/erro e o card
 * "médico selecionado + Alterar" vêm da base; aqui só delegamos o HTTP ao service.
 */
export class ReportEtiquetaEnderecoMedicoComponent extends RelatorioMedicoBase<Req, Resp> {
    private readonly etiquetaService = inject(EtiquetaEnderecoMedicoService);
    readonly etiquetaJobService = inject(EtiquetaEnderecoMedicoJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.etiquetaJobService.job$;

    protected get jobFacade(): RelatorioJobFacade {
        return this.etiquetaJobService;
    }

    /** Monta o request a partir do médico selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        return this.resolverIdentificacaoMedico('Selecione um médico (CRM/UF) antes de gerar a etiqueta.');
    }

    protected exportar(request: Req) {
        return this.etiquetaService.exportarPdf(request);
    }

    /** Limpa o job/preview corrente (a etiqueta não tem formulário de dados a resetar). */
    limparFormulario(): void {
        this.limparJob();
    }
}
